import { MenuIcon } from '@assets/icons'
import { ContactUsModal } from '@components/ContactUsButton/ContactUsModal'
import { useWithAmpli } from '@contexts/AmpliContextProvider'
import { useEnvConfig } from '@contexts/EnvConfigProvider'
import { useThemeMode } from '@contexts/ThemeContext'
import { useIsCorporateDataEnabled } from '@hooks/useCorporateData'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import BusinessIcon from '@mui/icons-material/Business'
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined'
import EmailIcon from '@mui/icons-material/Email'
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined'
import WebAssetIcon from '@mui/icons-material/WebAsset'
import { List, ListItemButton } from '@mui/material'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
	BottomDiv,
	Drawer,
	MenuButtonBox,
	Toolbar,
	VersionText,
} from './styles'
import { ToolbarItem } from './ToolbarItem'
import {
	ITEM_BASE_SX,
	StyledLabel,
	StyledListItemIcon,
} from './ToolbarItem/styles'

const Menu = () => {
	const { t } = useTranslation()
	const withAmpli = useWithAmpli()
	const { mode, toggleMode } = useThemeMode()
	const [contactOpen, setContactOpen] = useState(false)
	const handleContactOpen = useCallback(() => setContactOpen(true), [])
	const handleContactClose = useCallback(() => setContactOpen(false), [])

	const { isCorporateDataEnabled } = useIsCorporateDataEnabled()

	const version = useEnvConfig()?.COMMIT_HASH

	const handleCO2Click = useCallback(() => {
		withAmpli(ampli =>
			ampli.clickEvent({
				buttonText: 'Click CO2AI',
				clickPageUrl: window.location.href,
			})
		)
	}, [withAmpli])

	return (
		<Drawer variant="permanent">
			<Toolbar>
				<MenuButtonBox>
					<MenuIcon />
				</MenuButtonBox>
			</Toolbar>

			<List>
				<ToolbarItem
					text={t('menu.dashboard')}
					linkPath="/"
					IconComponent={SpaceDashboardOutlinedIcon}
				/>
				<ToolbarItem
					text={t('menu.myProducts')}
					linkPath="/my-products"
					IconComponent={Inventory2OutlinedIcon}
				/>
				<ToolbarItem
					text={t('menu.productEmissionsRequests')}
					linkPath="/products"
					IconComponent={EnergySavingsLeafIcon}
				/>
				<ToolbarItem
					text={t('menu.connectedOrganizations')}
					linkPath="/connected-organizations"
					IconComponent={AccountTreeOutlinedIcon}
				/>
				{isCorporateDataEnabled && (
					<ToolbarItem
						text={t('menu.corporateData')}
						linkPath="/corporate-data"
						IconComponent={BusinessIcon}
					/>
				)}
			</List>
			<BottomDiv>
				<List>
					<ToolbarItem
						text={t('menu.team')}
						linkPath="/settings?tab=teamMembers"
						IconComponent={GroupOutlinedIcon}
					/>
					<ToolbarItem
						isExternalUrl
						text={t('menu.helpCenter')}
						linkPath="https://knowledge.co2ai.com/ecosystem/how-to-start"
						IconComponent={HelpOutlineIcon}
					/>
					<ListItemButton
						disableGutters
						sx={ITEM_BASE_SX}
						onClick={handleContactOpen}
					>
						<StyledListItemIcon $isSelected={false}>
							<EmailIcon />
						</StyledListItemIcon>
						<StyledLabel $isSelected={false}>{t('menu.support')}</StyledLabel>
					</ListItemButton>
					<ContactUsModal open={contactOpen} onClose={handleContactClose} />
					<ToolbarItem
						text={t('menu.settings')}
						linkPath="/settings"
						IconComponent={SettingsOutlinedIcon}
					/>
					<ToolbarItem
						isExternalUrl
						text={t('menu.co2ai')}
						linkPath="https://www.co2ai.com/"
						IconComponent={WebAssetIcon}
						onClick={handleCO2Click}
					/>
					<ListItemButton disableGutters sx={ITEM_BASE_SX} onClick={toggleMode}>
						<StyledListItemIcon $isSelected={false}>
							{mode === 'dark' ? (
								<LightModeOutlinedIcon />
							) : (
								<DarkModeOutlinedIcon />
							)}
						</StyledListItemIcon>
						<StyledLabel $isSelected={false}>
							{mode === 'dark' ? t('menu.lightMode') : t('menu.darkMode')}
						</StyledLabel>
					</ListItemButton>
					<ToolbarItem
						text={t('menu.logout')}
						linkPath="/logout"
						IconComponent={LogoutOutlinedIcon}
					/>
				</List>
				{version && (
					<VersionText className="version-info" variant="caption">
						{t('menu.version', { version })}
					</VersionText>
				)}
			</BottomDiv>
		</Drawer>
	)
}

export { Menu }
